import '../css/base.css';

/*
 * Infinite scroll for the listings grid.
 *
 * The important design constraint is not the scrolling, it is the URLs. A
 * property portal lives on search traffic, and a grid that only exists after
 * JavaScript runs is invisible to crawlers — which for this site would be an
 * existential problem rather than a cosmetic one. So:
 *
 *   - page 1 is rendered server-side and is complete without JavaScript
 *   - a real <a href="/page/2/"> exists and works if JavaScript never runs
 *   - loading more pushes the real /page/N/ URL via the History API, so the
 *     address bar, the back button and sharing all behave
 *
 * The progressive-enhancement order matters: we only take over the link once we
 * know we can, so nothing is broken for a visitor we failed to serve.
 */

const SELECTORS = {
  grid:   '[data-mx-grid]',
  more:   '[data-mx-more]',
  status: '[data-mx-status]',
  sheet:  '[data-mx-sheet]',
  open:   '[data-mx-open]',
  close:  '[data-mx-close]',
  lightbox: '[data-mx-lightbox]',
  header: '.mx-header',
  bar:    '.mx-listing-head',
  scroller: '[data-mx-scroller]',
  map:    '[data-mx-map]',
  multiselect: '.mx-multiselect',
  shown:  '[data-mx-shown]',
  searchSummary: '[data-mx-search-summary]',
};

const state = {
  loading: false,
  done:    false,
};

/** Announce to assistive tech, and to anyone watching a slow connection. */
function setStatus(el, message) {
  if (el) el.textContent = message;
}

/** Placeholder cards, so the grid does not jump when results arrive. */
function showSkeletons(grid, count) {
  const frag = document.createDocumentFragment();
  for (let i = 0; i < count; i += 1) {
    const s = document.createElement('div');
    s.className = 'mx-skeleton';
    s.dataset.mxSkeleton = '';
    s.setAttribute('aria-hidden', 'true');
    s.innerHTML = '<div class="mx-skeleton__media"></div>'
                + '<div class="mx-skeleton__line"></div>'
                + '<div class="mx-skeleton__line"></div>';
    frag.appendChild(s);
  }
  grid.appendChild(frag);
}

function clearSkeletons(grid) {
  grid.querySelectorAll('[data-mx-skeleton]').forEach((n) => n.remove());
}

async function loadNext({ grid, more, status }) {
  if (state.loading || state.done) return;

  const nextUrl = more.getAttribute('href');
  if (!nextUrl) { state.done = true; return; }

  state.loading = true;
  more.setAttribute('disabled', '');
  setStatus(status, 'Loading more…');
  showSkeletons(grid, 4);

  try {
    // Ask for the same URL a browser would use, but signal that we want just
    // the grid fragment. One route serves both, so there is no second template
    // to keep in step with the first.
    const res = await fetch(nextUrl, {
      headers: { 'X-Requested-With': 'mx-listings' },
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const data = await res.json();
    clearSkeletons(grid);

    if (data.html) {
      grid.insertAdjacentHTML('beforeend', data.html);
		const shown = document.querySelector(SELECTORS.shown);
		if (shown) shown.textContent = grid.children.length.toLocaleString();

    }

    // The real URL, so the back button and sharing work. replaceState rather
    // than pushState: appending to a grid is not a new page from the visitor's
    // point of view, and pushing would make Back walk up one page per scroll.
    if (data.canonical) {
      window.history.replaceState({ mxPage: data.page }, '', data.canonical);
    }

    if (data.next) {
      more.setAttribute('href', data.next);
      more.removeAttribute('disabled');
      setStatus(status, '');
    } else {
      state.done = true;
      more.remove();
      setStatus(status, data.total
        ? `That is all ${data.total} of them.`
        : 'That is everything.');
    }
  } catch (err) {
    // Leave the link in place and usable. A failed fetch should degrade to the
    // thing that worked before JavaScript intervened, not strand the visitor.
    clearSkeletons(grid);
    more.removeAttribute('disabled');
    setStatus(status, 'Could not load more results. Try again.');
    // eslint-disable-next-line no-console
    console.error('[mx-listings]', err);
  } finally {
    state.loading = false;
  }
}

/*
 * The filter sheet.
 *
 * Everything hard about a modal — the focus trap, Escape to close, making the rest
 * of the page inert, returning focus to the trigger — is done by showModal(). This
 * function only opens and closes it, which is the entire argument for using a
 * <dialog> rather than a positioned div.
 *
 * The fields work as a plain block in the page when this never runs; the trigger
 * is hidden until the `mx-js` capability probe has confirmed showModal() exists.
 */
function initSheet() {
  const sheet = document.querySelector(SELECTORS.sheet);
  const open = document.querySelector(SELECTORS.open);
  if (!sheet || !open || typeof sheet.showModal !== 'function') return;

  const close = sheet.querySelector(SELECTORS.close);

  open.addEventListener('click', () => sheet.showModal());
  close?.addEventListener('click', () => sheet.close());

  // A click on the backdrop is outside the dialog's own box.
  sheet.addEventListener('click', (e) => {
    if (e.target !== sheet) return;
    const box = sheet.getBoundingClientRect();
    const outside = e.clientY < box.top || e.clientY > box.bottom
                 || e.clientX < box.left || e.clientX > box.right;
    if (outside) sheet.close();
  });
}

/* Keep enhanced GET URLs readable by omitting controls that carry no choice. */
function initFilterForm() {
  const form = document.querySelector('.mx-refine');
  if (!form) return;
  form.addEventListener('submit', () => {
    // Panning updates the shareable URL after the server rendered the form.
    const bbox = form.querySelector('[data-mx-search-bbox]');
    if (bbox) bbox.value = new URL(window.location.href).searchParams.get('bbox') || '';
    form.querySelectorAll('input[name], select[name]').forEach((field) => {
      if (field.value === '') field.disabled = true;
    });
  });
}

/** Let a completed mobile search use one slim results bar until editing is asked for. */
function initSearchSummary() {
  const button = document.querySelector(SELECTORS.searchSummary);
  const head = button?.closest('.mx-listing-head');
  if (!button || !head) return;

  button.addEventListener('click', () => {
    const expanded = head.classList.toggle('is-search-expanded');
    button.setAttribute('aria-expanded', String(expanded));
    button.textContent = expanded ? 'Hide search' : 'Change search';
  });
}

/** Keep compact checkbox disclosures behaving like familiar dropdowns. */
function initMultiselects() {
  const controls = [...document.querySelectorAll(SELECTORS.multiselect)];
  controls.forEach((control) => {
    const summary = control.querySelector('summary');
    const empty = control.dataset.empty || summary.textContent.trim();
    const singular = control.dataset.singular
      || control.closest('.mx-field-group')?.querySelector('.mx-label')?.textContent.trim().toLowerCase()
      || 'choice';
    const plural = control.dataset.plural || `${singular}s`;
    const update = () => {
      const count = control.querySelectorAll('input[type="checkbox"]:checked').length;
      summary.textContent = count
        ? `${count} ${count === 1 ? singular : plural} selected`
        : empty;
    };
    control.addEventListener('toggle', () => {
      if (control.open) controls.forEach((other) => { if (other !== control) other.open = false; });
    });
    control.addEventListener('change', update);
    update();
  });
}

/*
 * The gallery lightbox.
 *
 * Every thumbnail is already a real link to the full-size photograph, so this is
 * strictly an upgrade: if it never runs, clicking a photograph still shows the
 * photograph. Same top-layer reasoning as the filter sheet — showModal() gives the
 * focus trap, Escape and an inert background, and the dialog's containing block is
 * the viewport rather than any contained ancestor.
 */
function initLightbox() {
  const galleries = [{
    box: document.querySelector(SELECTORS.lightbox),
    links: [...document.querySelectorAll('[data-mx-photo]')],
  }];
  // Named image groups have their own viewer and never enter the photo gallery.
  document.querySelectorAll('[data-mx-image-viewer]').forEach((box) => {
    const name = box.getAttribute('data-mx-image-viewer');
    galleries.push({ box, links: [...document.querySelectorAll('[data-mx-image]')]
      .filter((link) => link.getAttribute('data-mx-image') === name) });
  });
  galleries.forEach(({ box, links }) => initImageViewer(box, links));
}

function initImageViewer(box, links) {
  if (!box || !links.length || typeof box.showModal !== 'function') return;

  const img = box.querySelector('[data-mx-lightbox-img]');
  const count = box.querySelector('[data-mx-lightbox-count]');
  let at = 0;
  let opener;
  const message = box.querySelector('[data-mx-lightbox-message]');
  const original = box.querySelector('[data-mx-lightbox-original]');

  function draw() {
    const link = links[at];
    const pdf = 'mxPdfFloorplan' in link.dataset;
    const src = link.dataset.mxViewerSrc || (pdf ? '' : link.getAttribute('href'));
    if (src) img.src = src;
    else img.removeAttribute('src');
    img.hidden = !src; img.alt = link.querySelector('img')?.alt ?? '';
    if (message) {
      message.hidden = Boolean(src);
      message.textContent = link.dataset.mxPdfState === 'failed' ? 'Preview unavailable. Open the original PDF below.' : 'Preparing floorplan preview…';
    }
    if (original) {
      original.hidden = !pdf; original.href = link.getAttribute('href');
      original.textContent = Number(link.dataset.mxPdfPages) > 1 ? `Open original PDF (${link.dataset.mxPdfPages} pages)` : 'Open original PDF';
    }
  }

  function show(i) {
    at = (i + links.length) % links.length;
    draw();
    if (count) count.textContent = `${at + 1} of ${links.length}`;
    box.dispatchEvent(new CustomEvent('mx-image-change', { detail: { index: at } }));
    if ('mxPdfFloorplan' in links[at].dataset && !links[at].dataset.mxViewerSrc) links[at].dispatchEvent(new CustomEvent('mx-document-preview-request'));
  }
  links.forEach((link, index) => link.addEventListener('mx-document-preview-ready', () => {
    if (index === at && box.open) draw();
  }));

  function open(e, a, i) {
    // Let a modified click do what the visitor asked: open in a new tab.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    opener = a;
    show(i);
    box.showModal();
  }
  links.filter((a) => !a.hasAttribute('data-mx-gallery-item')).forEach((a) => {
    a.addEventListener('click', (e) => open(e, a, links.indexOf(a)));
  });
  const group = box.getAttribute('data-mx-image-viewer');
  if (group) document.querySelectorAll('[data-mx-viewer-open]').forEach((a) => {
    if (a.getAttribute('data-mx-viewer-open') === group) {
      a.addEventListener('click', (e) => open(e, a, Number(a.getAttribute('data-mx-viewer-index')) || 0));
    }
  });

  box.querySelector('[data-mx-lightbox-next]')?.addEventListener('click', () => show(at + 1));
  box.querySelector('[data-mx-lightbox-prev]')?.addEventListener('click', () => show(at - 1));
  box.querySelector('[data-mx-lightbox-close]')?.addEventListener('click', () => box.close());
  box.addEventListener('close', () => opener?.focus());

  // Arrow keys, because a gallery that ignores them feels broken.
  box.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); show(at + 1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); show(at - 1); }
  });
}

/*
 * Publish the sticky furniture's real height as CSS custom properties.
 *
 *   --mx-header-h   where the search bar has to stick to sit under the masthead
 *   --mx-sticky-h   both together, which is html { scroll-padding-top }, and so
 *                   what stops a tabbed link or an anchor jump landing behind
 *                   them (WCAG 2.2 2.4.11 Focus Not Obscured)
 *
 * Measured rather than assumed because the masthead wraps: on a narrow screen the
 * navigation drops to its own line and the header grows by ~40px. A constant
 * would be wrong precisely where being wrong hides the search bar. The stylesheet
 * still carries fallbacks that err large, for a page where this never runs.
 */
/*
 * Held at module scope on purpose. A ResizeObserver referenced only by a local
 * inside the function that made it can be collected once that function returns,
 * and then it silently stops firing — which is exactly what happened: the
 * variables were measured once at 375px and still read 391px at 1280px, so the
 * search bar stuck 300px below the masthead with a strip of page between them.
 */
let stickyObserver;

function initSticky() {
  const header = document.querySelector(SELECTORS.header);
  if (!header) return;

  const bar = document.querySelector(SELECTORS.bar);
  const root = document.documentElement;
  let queued = false;

  const write = () => {
    queued = false;
    const h = header.getBoundingClientRect().height;
    root.style.setProperty('--mx-header-h', `${Math.round(h)}px`);

    /*
     * Only furniture that is actually stuck can hide a focused link, so only that
     * belongs in scroll-padding-top.
     *
     * Below 44rem the stylesheet takes the masthead out of the sticky flow
     * (position: static) and pins the search bar to the top instead. Adding the
     * masthead's height here regardless would have set scroll-padding-top to 342px
     * on a phone where only 113px is stuck — every anchor jump and every tabbed
     * link would land 229px further down the page than it should, which is a
     * different way of failing the same success criterion this is here to meet.
     *
     * CSS owns the decision and this reads it back, so the breakpoint lives in one
     * place rather than being duplicated as a matchMedia query that could disagree.
     */
    const headerSticks = getComputedStyle(header).position === 'sticky';
    const barH = bar ? bar.getBoundingClientRect().height : 0;
    const stuck = (headerSticks ? h : 0) + barH;
    root.style.setProperty('--mx-sticky-h', `${Math.round(stuck)}px`);
  };

  // Coalesce: a drag-resize fires continuously, and this reads layout.
  const measure = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(write);
  };

  write();

  /*
   * Three triggers, because each catches something the others miss: resize for
   * the viewport (which is what makes the navigation wrap), fonts for the swap
   * from the fallback face, and the observer for anything else that changes the
   * boxes — a plugin injecting a notice into the header, say.
   */
  window.addEventListener('resize', measure);
  document.fonts?.ready.then(measure);

  if ('ResizeObserver' in window) {
    stickyObserver = new ResizeObserver(measure);
    stickyObserver.observe(header);
    if (bar) stickyObserver.observe(bar);
  }
}

/*
 * Horizontal photograph strips.
 *
 * The strip is a native scroll container and works untouched — this only adds the
 * two buttons, and only once it knows there is something to scroll to. Scrolling
 * by a shade under one viewport width keeps a photograph in common between
 * before and after, so it reads as moving along a row rather than as paging.
 */
function initScrollers() {
  document.querySelectorAll(SELECTORS.scroller).forEach((wrap) => {
    const strip = wrap.querySelector('[data-mx-strip]');
    const prev = wrap.querySelector('[data-mx-scroll-prev]');
    const next = wrap.querySelector('[data-mx-scroll-next]');
    if (!strip || !prev || !next) return;

    const overflows = () => strip.scrollWidth - strip.clientWidth > 4;

    const sync = () => {
      if (!overflows()) {
        prev.hidden = true;
        next.hidden = true;
        return;
      }
      prev.hidden = false;
      next.hidden = false;
      // toggleAttribute, not a class: a disabled button is genuinely
      // unavailable, and the attribute is what tells assistive tech that.
      prev.toggleAttribute('disabled', strip.scrollLeft <= 2);
      next.toggleAttribute(
        'disabled',
        strip.scrollLeft >= strip.scrollWidth - strip.clientWidth - 2,
      );
    };

    const page = (dir) => strip.scrollBy({ left: dir * strip.clientWidth * 0.85 });

    prev.addEventListener('click', () => page(-1));
    next.addEventListener('click', () => page(1));
    strip.addEventListener('scroll', sync, { passive: true });

    if ('ResizeObserver' in window) new ResizeObserver(sync).observe(strip);
    sync();
  });
}

/*
 * Zoom and pan the drawn map, and make the list follow it.
 *
 * Two layers, deliberately:
 *
 *   1. INSTANT. Every pin carries its own position and its listing id, so as the
 *      viewBox changes we can hide the pins and cards that have left the view
 *      without waiting for anybody. This is what makes it feel like a map.
 *
 *   2. CORRECT. The browser only holds this page's listings, so "what is in this
 *      bay" cannot be answered here — there may be forty more properties in it
 *      further down the catalogue. So the visible box is also sent to the server as
 *      ?bbox=south,west,north,east, and the real answer replaces the instant one.
 *
 * The URL is updated with replaceState so a zoomed view can be shared and survives
 * a reload, and the whole thing is an enhancement: with no JavaScript you get the
 * whole island and the whole list, which is a perfectly good page.
 */
function initMap() {
  const figure = [...document.querySelectorAll(SELECTORS.map)]
    .find((candidate) => !candidate.closest('[data-mx-vector-map]'));
  const svg = figure?.querySelector('[data-mx-proj]');
  if (!figure || !svg) return;

  const proj = JSON.parse(svg.dataset.mxProj);
  const pins = [...svg.querySelectorAll('[data-mx-pin]')].map((el) => {
    const [x, y] = el.dataset.mxXy.split(' ').map(Number);
    return { el, x, y, id: el.dataset.mxPin };
  });
  const list = document.querySelector(SELECTORS.grid);

  // The whole island, and the tightest we will go: about 400m across, which is a
  // street's worth of context — as far as a coastline-only map can usefully go.
  const home = { x: 0, y: 0, w: proj.w, h: proj.h };
  const MIN_W = proj.w / 24;
  let view = { ...home };

  const apply = () => {
    svg.setAttribute('viewBox', `${view.x} ${view.y} ${view.w} ${view.h}`);
    // Keep pins and labels a constant size on screen however far we are zoomed in,
    // or a pin becomes a blob the size of Peel.
    svg.style.setProperty('--mx-map-k', String(view.w / proj.w));
    mark();
  };

  /** Hide what is off screen — the instant half. */
  const mark = () => {
    const inView = new Set();
    pins.forEach((p) => {
      const visible = p.x >= view.x && p.x <= view.x + view.w
                   && p.y >= view.y && p.y <= view.y + view.h;
      p.el.style.display = visible ? '' : 'none';
      if (visible) inView.add(p.id);
    });

    // Only filter the list once the map is actually zoomed: at full extent every
    // listing belongs, including the ones with no coordinates at all.
    const zoomed = view.w < proj.w - 1;
    list?.querySelectorAll('.mx-card').forEach((card) => {
      card.hidden = zoomed && !inView.has(card.dataset.mxId);
    });
  };

  /* viewBox back to latitude and longitude: the inverse of mx_map_project(). */
  const bounds = () => {
    const lng = (px) => (((proj.minX + (px - proj.pad) / proj.scale) * 180) / Math.PI);
    const lat = (py) => {
      const merc = proj.maxY - (py - proj.pad) / proj.scale;
      return ((2 * Math.atan(Math.exp(merc)) - Math.PI / 2) * 180) / Math.PI;
    };
    return {
      west: lng(view.x),
      east: lng(view.x + view.w),
      north: lat(view.y),
      south: lat(view.y + view.h),
    };
  };

  let timer;
  const publish = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      const url = new URL(window.location.href);
      if (view.w >= proj.w - 1) {
        url.searchParams.delete('bbox');
      } else {
        const b = bounds();
        url.searchParams.set('bbox', [b.south, b.west, b.north, b.east].map((n) => n.toFixed(5)).join(','));
      }
      window.history.replaceState({}, '', url);
      refresh(url);
    }, 450);
  };

  /** Ask the server what is really in the box, and swap the list for its answer. */
  async function refresh(url) {
    if (!list) return;
    try {
      const res = await fetch(url, {
        headers: { 'X-Requested-With': 'mx-listings' },
        credentials: 'same-origin',
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.html) {
        list.innerHTML = data.html;
        mark();
      }
    } catch {
      // The instant filter is already showing something reasonable; leave it.
    }
  }

  const zoom = (factor, cx, cy) => {
    const w = Math.min(home.w, Math.max(MIN_W, view.w * factor));
    const k = w / view.w;
    view = {
      w,
      h: view.h * k,
      x: cx - (cx - view.x) * k,
      y: cy - (cy - view.y) * k,
    };
    // Stay over the island.
    view.x = Math.max(-view.w * 0.2, Math.min(view.x, home.w - view.w * 0.8));
    view.y = Math.max(-view.h * 0.2, Math.min(view.y, home.h - view.h * 0.8));
    apply();
    publish();
  };

  /** Where in viewBox units did that pointer event happen? */
  const at = (e) => {
    const r = svg.getBoundingClientRect();
    // preserveAspectRatio="meet" letterboxes, so the drawn area is not the box.
    const s = Math.min(r.width / view.w, r.height / view.h);
    const ox = (r.width - view.w * s) / 2;
    const oy = (r.height - view.h * s) / 2;
    return [
      view.x + (e.clientX - r.left - ox) / s,
      view.y + (e.clientY - r.top - oy) / s,
    ];
  };

  figure.querySelector('[data-mx-zoom="in"]')?.addEventListener('click', () => zoom(1 / 1.6, view.x + view.w / 2, view.y + view.h / 2));
  figure.querySelector('[data-mx-zoom="out"]')?.addEventListener('click', () => zoom(1.6, view.x + view.w / 2, view.y + view.h / 2));
  figure.querySelector('[data-mx-zoom="reset"]')?.addEventListener('click', () => {
    view = { ...home };
    apply();
    publish();
  });

  svg.addEventListener('wheel', (e) => {
    e.preventDefault();
    const [cx, cy] = at(e);
    zoom(e.deltaY > 0 ? 1.2 : 1 / 1.2, cx, cy);
  }, { passive: false });

  // Drag to pan. Pointer events, so a finger and a mouse are the same code.
  let from = null;
  svg.addEventListener('pointerdown', (e) => {
    // Leave clicks on a pin alone: it is a link.
    if (e.target.closest('[data-mx-pin]')) return;
    from = { at: at(e), x: view.x, y: view.y };
    svg.setPointerCapture(e.pointerId);
    svg.style.cursor = 'grabbing';
  });
  svg.addEventListener('pointermove', (e) => {
    if (!from) return;
    const [cx, cy] = at(e);
    view.x = from.x + (from.at[0] - cx);
    view.y = from.y + (from.at[1] - cy);
    apply();
  });
  const release = () => {
    if (!from) return;
    from = null;
    svg.style.cursor = '';
    publish();
  };
  svg.addEventListener('pointerup', release);
  svg.addEventListener('pointercancel', release);

  // Arrive already zoomed, if the URL says so, by reversing the projection.
  const initial = new URL(window.location.href).searchParams.get('bbox');
  if (initial) {
    const [s, w, n, e] = initial.split(',').map(Number);
    if ([s, w, n, e].every(Number.isFinite)) {
      const px = (lng) => proj.pad + ((lng * Math.PI) / 180 - proj.minX) * proj.scale;
      const py = (lat) => proj.pad + (proj.maxY - Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360))) * proj.scale;
      view = { x: px(w), y: py(n), w: px(e) - px(w), h: py(s) - py(n) };
    }
  }
  apply();
}

const documentAssetBase = import.meta.url.slice(0, import.meta.url.lastIndexOf('/') + 1);

function init() {
  initSticky();
  if (document.querySelector('[data-mx-vector-map]')) {
    import('./vector-map.js').then(({ initVectorMaps }) => initVectorMaps()).catch(() => {});
  }
  initMap();
  initFilterForm();
  initSearchSummary();
  initMultiselects();
  initSheet();
  initLightbox();
  if (document.querySelector('[data-mx-document-gallery], [data-mx-pdf-preview]')) {
    import('./documents.js').then(({ initDocuments }) => initDocuments(documentAssetBase)).catch(() => {});
  }
  initScrollers();

  const grid   = document.querySelector(SELECTORS.grid);
  const more   = document.querySelector(SELECTORS.more);
  const status = document.querySelector(SELECTORS.status);
  if (!grid || !more) return;

  // Only now do we take the link over: everything above this point works
  // without us.
  more.addEventListener('click', (e) => {
    e.preventDefault();
    loadNext({ grid, more, status });
  });

  // Observe the real pagination link in the content, never the fixed footer.
  // Newly appended cards move the link away again; no scroll anchoring is needed.
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) loadNext({ grid, more, status });
    });
    io.observe(more);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
